import { capitalize } from "client/utils/capitalize";
import { convertFromPixels } from "client/utils/pixels/convertFromPixels";
import { truncateImageTitle } from "client/utils/truncateImageTitle";
import { DotPulse } from "components/common/Animation/DotPulse/DotPulse";
import { CloseIcon } from "components/common/Icons/CloseIcon";
import { InfoIcon } from "components/common/Icons/InfoIcon";
import { RightIcon } from "components/common/Icons/RightIcon";
import { LinearProgress } from "components/common/LinearProgress/LinearProgress";
import { ToolTip } from "components/common/ToolTip/ToolTip";
import { BulkResizeImage } from "store/features/bulkResize/types";
import { useBulkResizerPreviewImage } from "./useBulkResizerPreviewImage";
import { MAX_RESIZE_SIZE_IN_PX } from "store/features/bulkResize/utils/validateResizeDimensions";
import { roundFloat } from "client/utils/roundFloat";
import { useWindowSize } from "client/hooks/utils/useWindowSize";
import { translateFunc } from "client/utils/translateFunc";
import { Crop, RotateCw } from "lucide-react";
import BulkResizerSingleImageCropModal from "../../SingleImageCrop/BulkResizerSingleImageCropModal";
import { BulkResizerImageProvider } from "../../BulkResizerImageProvider";
import BulkResizerSingleImageRotateModal from "../../SingleImageRotate/FlipRotateModal";
import { useAppDispatch, useAppSelector } from "@/store/store";
import { setEditImageModal } from "@/store/features/bulkResize/actions";

export interface PreviewImageProps {
    image: BulkResizeImage;
    index: number;
}


export const PreviewImage = ({ image, index }: PreviewImageProps) => {
    const { handleDelete, unit, dimensionsValid, dpi, widthValid, heightValid, dimensionsErrorMessage, src } =
        useBulkResizerPreviewImage(image);
    const editImageModal = useAppSelector((store) => store.bulkResize.editImageModal);
    const { width } = useWindowSize();
    const dispatch = useAppDispatch();

    console.log("Rendering delete button with index:", index);

    return (
        <div className="group flex flex-col justify-end bg-irGray-200 dark:bg-darkSurface-300">
            <div className="flex justify-end items-center mt-1 cursor-pointer gap-x-2 px-2">
                {image.isCorrupted ? null : (
                    <>
                        <ToolTip text={translateFunc("crop-image")} position={{ y: "down" }}>
                            <div
                                className="cursor-pointer flex items-center justify-center rounded-full bg-dark-gray dark:bg-black bg-opacity-50 h-6 w-6"
                                data-testid = "edit-type-crop"
                                onClick={() => dispatch(setEditImageModal({ imageId: image.id, operation: "crop" }))}
                            >
                                <Crop className="w-4 h-4" color="white" />
                            </div>
                        </ToolTip>

                        <ToolTip text={translateFunc("rotate-and-flip-image")} position={{ y: "down" }}>
                            <div className="cursor-pointer flex items-center justify-center rounded-full bg-dark-gray dark:bg-black bg-opacity-50 h-6 w-6"
                            data-testid = "edit-type-rotate"
                            >
                                <RotateCw
                                    className="w-4 h-4"
                                    color="white"
                                    onClick={() =>
                                        dispatch(setEditImageModal({ imageId: image.id, operation: "flip-rotate" }))
                                    }
                                />
                            </div>
                        </ToolTip>
                    </>
                )}

                <ToolTip text={`${image.original.size}`} position={{ y: "down" }} showInMobile={true}>
                    <div className="cursor-pointer flex items-center justify-center rounded-full bg-dark-gray dark:bg-black bg-opacity-50 h-6 w-6">
                        <InfoIcon className="w-3 h-3" />
                    </div>
                </ToolTip>

                <ToolTip text={capitalize(translateFunc("remove-image"))} position={{ y: "down" }}>
                    <div
                        className="cursor-pointer flex items-center justify-center rounded-full bg-dark-gray dark:bg-darkSurface-100 bg-opacity-50 h-6 w-6"
                        data-testid={`image-${index}-delete-image`}
                        onClick={handleDelete}
                    >
                        <CloseIcon className="stroke-white" height={10} width={10} strokeWidth={2} />
                    </div>
                </ToolTip>
            </div>

            <BulkResizerImageProvider>
                <BulkResizerSingleImageCropModal
                    image={image}
                    isOpen={editImageModal?.imageId === image.id && editImageModal.operation === "crop"}
                    onClose={() => dispatch(setEditImageModal(null))}
                />

                <BulkResizerSingleImageRotateModal
                    image={image}
                    isOpen={editImageModal?.imageId === image.id && editImageModal.operation === "flip-rotate"}
                    onClose={() => dispatch(setEditImageModal(null))}
                />
            </BulkResizerImageProvider>

            <div className="relative flex flex-col items-start justify-between p-4 h-full">
                <img src={src} className="mx-auto max-h-[250px]" data-testid={`image-${index}-preview-image`} />
                {image.isCorrupted && (
                    <div className="text-md text-center text-red-500 font-semibold">
                        {translateFunc("corrupt-image-helper-text")}
                    </div>
                )}
            </div>
            <div className="flex flex-col py-4 px-2 gap-4 bg-white dark:bg-darkSurface-200 dark:text-white border-[0.25px] border-[#e2e8f0] dark:border-darkSurface-400">
                <div className="w-full max-w-full relative">
                    <div className="text-lg font-semibold overflow-ellipsis whitespace-nowrap">
                        <ToolTip text={image.title}>
                            <span data-testid={`image-${index}-title`}>
                                {truncateImageTitle(image.title, width < 1250 ? 10 : 15)}
                            </span>
                        </ToolTip>
                    </div>
                </div>
                {image.state === "imported" && (
                    <>
                        <div className="flex items-center gap-2 text-sm">
                            <ToolTip text={translateFunc("original-size-in-unit", { unit: translateFunc(unit) })}>
                                <div
                                    className="rounded-lg py-1 px-2 bg-[#abb8c3] dark:bg-darkSurface-300"
                                    data-testid={`image-${index}-original-dimensions`}
                                >
                                    {`${roundFloat(convertFromPixels(image.original.width, unit, +dpi))} X ${roundFloat(
                                        convertFromPixels(image.original.height, unit, +dpi),
                                    )}`}
                                </div>
                            </ToolTip>
                            <RightIcon />
                            <ToolTip
                                text={
                                    dimensionsValid
                                        ? capitalize(
                                              translateFunc("output-size-in-unit", { unit: translateFunc(unit) }),
                                          )
                                        : `${translateFunc("maximum-dimension-is")} ${roundFloat(
                                              convertFromPixels(MAX_RESIZE_SIZE_IN_PX, unit, +dpi),
                                              false,
                                          )} ${translateFunc(unit)} ${
                                              unit !== "pixels" ? translateFunc("at-dpi", { dpi }) : ""
                                          }`
                                }
                                data-testid={`image-${index}-original-dimensions`}
                            >
                                <div
                                    className={`rounded-lg text-white py-1 px-2 ${
                                        dimensionsValid
                                            ? "bg-[#7bdcb5] dark:bg-darkPrimary-300 dark:text-black"
                                            : "bg-[#DB1436]"
                                    }`}
                                    data-testid={`image-${index}-preview-dimensions`}
                                >
                                    {`${roundFloat(Number(image.preview.width))} X ${roundFloat(
                                        Number(image.preview.height),
                                    )}`}
                                </div>
                            </ToolTip>
                        </div>
                        {(!widthValid || !heightValid) && (
                            <div className="text-validation-error">{dimensionsErrorMessage}</div>
                        )}
                    </>
                )}
                {image.state === "added" && (
                    <div className="flex items-center justify-center bg-white dark:bg-darkSurface-200 w-full rounded-xl text-center overflow-hidden border border-irGray-200 dark:border-darkSurface-400">
                        <div className="py-2">
                            <DotPulse
                                options={{
                                    color: "var(--blue)",
                                }}
                            />
                        </div>
                    </div>
                )}
                {image.state === "importing" && (
                    <div className="flex items-center justify-center bg-white dark:bg-darkSurface-200 w-full rounded-xl text-center overflow-hidden border border-irGray-200 dark:border-darkSurface-400">
                        {image.importing?.progress === "continuous" ? (
                            <div className="py-2" data-testid={`text-${index}-importing`}>
                                <DotPulse
                                    options={{
                                        color: "var(--blue)",
                                    }}
                                />
                            </div>
                        ) : (
                            <LinearProgress
                                text={`${image.importing?.progress ?? 0}% of ${image.original.size}`}
                                percentage={image.importing?.progress ?? "continuous"}
                                testId={`text-${index}-importing`}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
